import { useCallback, useMemo, useState } from "react";
import { ResponsiveLine } from "@nivo/line";
import type { LineSeries, PointTooltipProps } from "@nivo/line";
import type { DatumId } from "@nivo/pie";
import { t } from "@/i18n-js/instance";
import { abbreviateNumber } from "@/react/helpers/number";
import { CHART_TYPES } from "@/react/hooks/analytics/chartTypes";
import { useThemeContext } from "@circle-react/providers";
import { ChartContainer } from "../../shared/ChartContainer";
import { EmptyState } from "../../shared/EmptyState";
import { LegendComponent } from "../../shared/LegendComponent";
import { LoadingChartComponent } from "../../shared/LoadingChartComponent";
import { useContainerSize } from "../../shared/hooks/useContainerSize";
import { useTickValues } from "../../shared/hooks/useTickValues";
import { ChartTooltip } from "../shared/Tooltip";
import type { ChartDataItem } from "../types";
import { baseAxisProps, lineChartTheme } from "./constants";
import { formatLineChartData } from "./utils";

interface LineChartComponentProps {
  className?: string;
  colors: string[];
  curve?:
    | "linear"
    | "cardinal"
    | "catmullRom"
    | "monotoneX"
    | "monotoneY"
    | "natural"
    | "step"
    | "stepAfter"
    | "stepBefore";
  data: ChartDataItem[];
  emptyStateDescription?: string;
  emptyStateTitle?: string;
  enableArea?: boolean;
  enableGridX?: boolean;
  enableGridY?: boolean;
  enablePoints?: boolean;
  indexBy: string;
  isLoading?: boolean;
  prefix?: string;
  rootLocale: string[];
  seriesKeys: string[];
  suffix?: string;
  title: string;
  tooltip?: string;
}

export const LineChartComponent = ({
  isLoading,
  className,
  title,
  data,
  tooltip,
  indexBy,
  rootLocale,
  seriesKeys,
  colors,
  prefix,
  suffix,
  curve = "monotoneX",
  enableArea = true,
  enablePoints = true,
  enableGridX = false,
  enableGridY = true,
  emptyStateTitle,
  emptyStateDescription,
}: LineChartComponentProps) => {
  const { containerWidth, containerRef } = useContainerSize();
  const [activeId, setActiveId] = useState<DatumId | null>(null);

  const activeColors = seriesKeys.map((key, index) =>
    activeId && activeId !== key ? `${colors[index]}80` : colors[index],
  );

  const { isLightAppearance } = useThemeContext();

  const tickValues = useTickValues({
    data,
    containerWidth,
    indexBy,
  });

  const formattedData = useMemo(
    () => formatLineChartData(data, seriesKeys, indexBy),
    [data, seriesKeys, indexBy],
  );

  const renderTooltip = useCallback(
    (props: PointTooltipProps<LineSeries>) => {
      const xValue = props.point.data.x;
      const tooltipData = formattedData.map((series, index) => {
        const point = series.data.find(d => d.x === xValue);
        const value = point?.y || 0;
        const total = formattedData.reduce((acc, s) => {
          const p = s.data.find(d => d.x === xValue);
          return acc + (p?.y || 0);
        }, 0);

        return {
          id: series.id,
          value,
          label: series.id,
          color: colors[index],
          formattedPercentage:
            total > 0 ? `${((value / total) * 100).toFixed(1)}%` : "0.0%",
        };
      });

      return (
        <ChartTooltip
          data={tooltipData}
          selected={props.point.seriesId as DatumId}
          prefix={prefix}
          suffix={suffix}
          rootLocale={rootLocale}
        />
      );
    },
    [formattedData, colors, prefix, suffix, rootLocale],
  );

  const legendData = useMemo(
    () =>
      seriesKeys.map((key, index) => ({
        id: key,
        color: activeColors[index],
        label: key,
      })),
    [seriesKeys, activeColors],
  );

  if (isLoading) {
    return <LoadingChartComponent type="line" className={className} />;
  }

  if (!data.length) {
    return (
      <EmptyState
        chartTitle={title}
        tooltip={tooltip}
        className={className}
        emptyTitle={emptyStateTitle}
        emptyDescription={emptyStateDescription}
      />
    );
  }

  const theme = lineChartTheme(Boolean(isLightAppearance));

  return (
    <ChartContainer
      title={title}
      tooltip={tooltip}
      className={className}
      data={data}
      chartType={CHART_TYPES.LINE}
    >
      <div className="flex flex-col items-center gap-2">
        <div
          ref={containerRef}
          className="mb-4 flex h-80 w-full overflow-visible"
        >
          <ResponsiveLine
            data={formattedData}
            margin={{ top: 20, right: 20, bottom: 30, left: 60 }}
            xScale={{ type: "point" }}
            yScale={{
              type: "linear",
              min: "auto",
              max: "auto",
              stacked: false,
              reverse: false,
            }}
            curve={curve}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              ...baseAxisProps,
              legendOffset: 32,
              tickValues: tickValues,
            }}
            axisLeft={{
              ...baseAxisProps,
              legendOffset: -60,
              format: abbreviateNumber,
            }}
            enableGridX={enableGridX}
            enableGridY={enableGridY}
            gridYValues={5}
            colors={activeColors}
            pointSize={enablePoints ? 6 : 0}
            pointColor={{ theme: "background" }}
            pointBorderWidth={2}
            pointBorderColor={{ from: "serieColor" }}
            pointLabelYOffset={-12}
            enableArea={enableArea}
            areaOpacity={0.15}
            useMesh
            onMouseEnter={datum => {
              if ("seriesId" in datum) {
                setActiveId(datum.seriesId as DatumId);
              }
            }}
            onMouseLeave={() => setActiveId(null)}
            tooltip={renderTooltip}
            theme={theme}
            ariaLabel={t("settings.analytics.line_chart")}
          />
        </div>
      </div>
      <LegendComponent
        data={legendData}
        orientation="horizontal"
        setActiveId={setActiveId}
        rootLocale={rootLocale}
      />
    </ChartContainer>
  );
};

LineChartComponent.displayName = "LineChartComponent";
